import { ButtonProps } from "@mui/material/Button/Button";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import { v4 } from "uuid";
import { Formik, FormikProps } from "formik";
import * as Yup from "yup";
import { useCallback, useMemo, useRef, useState } from "react";
import { Dialog } from "../../../components/Dialog";
import { Fields } from "../../../components/Forms/Fields";
import { Infos } from "../../../components/Forms/Infos";
import { useToast } from "../../../hooks/useToast";
import { useStyles } from "../styles";
import { CreateFormData } from "../../../types/forms";
import { Colors } from "../../../components/Forms/Colors";
import { FIELDS_TYPE_FORMS } from "../../../utils/constants";
import { Review } from "../../../components/Forms/Review";
import { map } from "lodash";
import { useMutation } from "react-query";
import { createForm } from "../../../services/forms";
import { queryClient } from "../../../services/queryClient";

type NewFormsProps = {
  open: boolean;
  onClose: () => void;
};

const validationSchema = Yup.object().shape({
  typeForm: Yup.string().required("Tipo inválida"),
  nameForm: Yup.string().required("Nome obrigatório"),
  fontColorForm: Yup.string().required("Cor da fonte obrigatório"),
  bgColorForm: Yup.string().required("Cor de fundo obrigatório"),
  fields: Yup.array()
    .when("typeForm", {
      is: (typeForm) => {
        return typeForm ? typeForm === "ADD_CLIENTS" : false;
      },
      then: Yup.array().of(
        Yup.object().shape({
          active: Yup.string(),
          label: Yup.string().required("Pergunta obrigatório"),
          required: Yup.boolean(),
          key: Yup.object().shape({
            value: Yup.string().required("O identificador é obrigatório"),
            label: Yup.string(),
          }),
          inputType: Yup.object()
            .shape({
              value: Yup.string(),
              label: Yup.string(),
            })
            .required("Tipo do campo obrigatório"),
          optionSelect: Yup.array().nullable().of(Yup.string()),
        })
      ),
      otherwise: Yup.array().of(
        Yup.object().shape({
          active: Yup.string(),
          label: Yup.string().required("Pergunta obrigatório"),
          required: Yup.boolean(),
          key: Yup.object().shape({
            value: Yup.string(),
            label: Yup.string(),
          }),
          inputType: Yup.object()
            .shape({
              value: Yup.string(),
              label: Yup.string(),
            })
            .required("Tipo do campo obrigatório"),
          optionSelect: Yup.array().nullable().of(Yup.string()),
        })
      ),
    })
    .when("typeForm", {
      is: (typeForm) => {
        return typeForm ? typeForm === "CUSTOMER_LIKES" : false;
      },
      then: (schema) => {
        return schema.max(6, "Formulário atingiu o máximo de campos");
      },
    })
    .min(1, "Formulário precisa de um campo válido")

    .required("Campo obrigatório"),
});

export function NewForms({ onClose, open }: NewFormsProps) {
  const formikRef = useRef<FormikProps<CreateFormData>>(null);

  const toast = useToast();
  const styles = useStyles();
  const [activeStep, setActiveStep] = useState<number>(0);

  const newForm = useMutation(
    async (data: CreateFormData) => {
      const response = await createForm(data);
      return response.message;
    },
    {
      onSuccess: (message) => {
        toast.show(message, { severity: "success" });
        queryClient.invalidateQueries(["forms"]);
        handleClose();
      },
      onError: () => {
        toast.show("Erro ao criar o formulário", { severity: "error" });
      },
    }
  );

  const steps = useMemo(() => {
    return ["Informações", "Campos", "Cores", "Resumo"];
  }, []);

  const handleSave = useCallback(
    async (data: CreateFormData) => {
      await newForm.mutateAsync(data);
    },
    [newForm]
  );

  const handleClose = useCallback(() => {
    setActiveStep(0);
    onClose();
  }, [onClose, setActiveStep]);

  const handleValidateStep = useCallback((): Promise<boolean> => {
    return new Promise(async (resolve, reject) => {
      try {
        switch (activeStep) {
          case 0:
            await formikRef.current?.validateField("typeForm");
            await formikRef.current?.validateField("nameForm");
            break;
          case 1:
            await formikRef.current?.validateField("fields");
            break;
        }

        resolve(formikRef.current?.isValid);
      } catch (err) {
        reject(false);
      }
    });
  }, [activeStep, formikRef]);

  const handleGoBack = useCallback(() => {
    formikRef.current?.setErrors({});
    setActiveStep((prev) => prev - 1);
  }, []);

  const handleGoNext = useCallback(async () => {
    try {
      const isValid = await handleValidateStep();
      if (isValid) {
        setActiveStep((prev) => prev + 1);
      }
    } catch (err) {
      console.log(err);
    }
  }, [handleValidateStep]);

  const actions: ButtonProps[] = useMemo(() => {
    return [
      {
        children: "Voltar",
        onClick: () => handleGoBack(),
        disabled: activeStep === 0 || newForm.isLoading,
      },
      {
        disabled: newForm.isLoading,
        children: newForm.isLoading
          ? "Enviado solicitação..."
          : activeStep === 3
          ? "Criar"
          : "Continuar",

        variant: "contained",
        onClick: () =>
          activeStep === 3 ? formikRef.current?.handleSubmit() : handleGoNext(),
      },
    ];
  }, [activeStep, formikRef, newForm.isLoading, handleGoNext, handleGoBack]);

  const renderContent = useCallback(() => {
    switch (activeStep) {
      case 0:
        return <Infos />;
      case 1:
        return <Fields />;
      case 2:
        return <Colors />;
      case 3:
        return <Review />;
      default:
        return <></>;
    }
  }, [activeStep]);

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      title="Novo formulário"
      open={open}
      actions={actions}
      onClose={handleClose}
    >
      <Stepper activeStep={activeStep}>
        {map(steps, (step) => {
          return (
            <Step key={step}>
              <StepLabel>{step}</StepLabel>
            </Step>
          );
        })}
      </Stepper>
      <div className={styles.rootContent}>
        <Formik
          innerRef={formikRef}
          initialValues={
            {
              fields: [
                {
                  inputType: FIELDS_TYPE_FORMS[0],
                  active: 1,
                  required: 0,
                  guid: v4(),
                },
              ],
              bgColorForm: "#FFF",
              fontColorForm: "#000",
              typeForm: "QUIZ",
            } as CreateFormData
          }
          validationSchema={validationSchema}
          validateOnChange={false}
          onSubmit={handleSave}
        >
          {renderContent()}
        </Formik>
      </div>
    </Dialog>
  );
}
